(() => {
  if (customElements.get('luemas-hero-slider')) return;

  class LuemasHeroSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.resizeTimer = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isPointerInside = false;
      this.isFocusInside = false;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-hero-viewport]');
      this.track = this.querySelector('[data-hero-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.prevButton = this.querySelector('[data-hero-prev]');
      this.nextButton = this.querySelector('[data-hero-next]');

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(Number(this.dataset.autoplayDelay) || 6000, 2000);
      this.transitionSpeed = Math.max(Number(this.dataset.transitionSpeed) || 700, 0);
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';

      this.track.style.transitionDuration = `${this.transitionSpeed}ms`;
      this.bindEvents();
      this.updateSlider(false);
      this.syncViewportHeight();
      this.startAutoplay();

      this.images = Array.from(this.querySelectorAll('img'));
      this.images.forEach((image) => {
        if (!image.complete) image.addEventListener('load', () => this.syncViewportHeight(), { once: true });
      });

      this.videos = Array.from(this.querySelectorAll('video'));
      this.videos.forEach((video) => {
        video.addEventListener('loadedmetadata', () => this.syncViewportHeight(), { once: true });
      });
      this.syncMediaPlayback();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncViewportHeight());
        this.resizeObserver.observe(this.slides[0]);
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    bindEvents() {
      this.prevButton?.addEventListener('click', () => {
        this.goTo(this.currentIndex - 1, true);
      });

      this.nextButton?.addEventListener('click', () => {
        this.goTo(this.currentIndex + 1, true);
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(Number(dot.dataset.heroDot), true);
        });
      });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => {
          this.isPointerInside = true;
          this.stopAutoplay();
        });
        this.addEventListener('mouseleave', () => {
          this.isPointerInside = false;
          this.startAutoplay();
        });
      }

      this.addEventListener('focusin', () => {
        this.isFocusInside = true;
        this.stopAutoplay();
      });

      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.isFocusInside = false;
          this.startAutoplay();
        }
      });

      this.viewport.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }, { passive: true });

      this.viewport.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.goTo(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1, true);
        }
      }, { passive: true });

      this.track.addEventListener('transitionend', () => this.syncViewportHeight());
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const selectedSlide = event.target.closest('[data-hero-index]');
          if (!selectedSlide || !this.contains(selectedSlide)) return;
          this.stopAutoplay();
          this.goTo(Number(selectedSlide.dataset.heroIndex), false);
        });

        this.addEventListener('shopify:block:deselect', () => this.startAutoplay());
      }
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.syncViewportHeight();
        this.syncMediaPlayback();
      }, 120);
    }

    onVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    goTo(index, userInitiated = false) {
      if (this.slides.length <= 1) return;
      const nextIndex = (index + this.slides.length) % this.slides.length;
      const currentHeight = this.slides[this.currentIndex]?.offsetHeight || 0;
      const nextHeight = this.slides[nextIndex]?.offsetHeight || 0;

      if (currentHeight || nextHeight) {
        this.viewport.style.height = `${Math.max(currentHeight, nextHeight)}px`;
      }

      this.currentIndex = nextIndex;
      this.updateSlider(true);

      window.setTimeout(() => this.syncViewportHeight(), this.transitionSpeed + 30);

      if (userInitiated) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    updateSlider(animate = true) {
      if (!animate) this.track.style.transitionDuration = '0ms';
      this.track.style.transform = `translate3d(-${this.currentIndex * 100}%, 0, 0)`;

      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.toggleAttribute('inert', !isActive);
      });

      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });

      this.syncMediaPlayback();

      if (!animate) {
        requestAnimationFrame(() => {
          this.track.style.transitionDuration = `${this.transitionSpeed}ms`;
        });
      }
    }

    syncViewportHeight() {
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide || !this.viewport) return;
      const height = activeSlide.offsetHeight;
      if (height > 0) this.viewport.style.height = `${height}px`;

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver.observe(activeSlide);
      }
    }


    syncMediaPlayback() {
      if (!this.slides) return;
      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;
        slide.querySelectorAll('video').forEach((video) => {
          const wrapper = video.closest('.luemas-hero-v4__video-wrap');
          const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
          const hiddenForViewport =
            (wrapper?.classList.contains('luemas-hero-v4__video-wrap--desktop') && isMobileViewport) ||
            (wrapper?.classList.contains('luemas-hero-v4__video-wrap--mobile') && !isMobileViewport);

          if (!isActive || hiddenForViewport) {
            video.pause();
            return;
          }
          if (video.autoplay) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
          }
        });
      });
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.slides.length <= 1 || document.hidden || this.isFocusInside || (this.pauseOnHover && this.isPointerInside)) return;
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1, false), this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('luemas-hero-slider', LuemasHeroSlider);
})();
